import { useMemo } from "react";

import { MEDIA_TYPES } from "~/components/gallery/media-type.const";
import type { MediaType } from "~/components/gallery/photo-gallery.types";
import type { FormattedImage, IVideoEmbedData } from "~/types/api-types";

interface UseMediaFilterProps {
  media: (IVideoEmbedData & FormattedImage)[];
  activeMediaType: MediaType;
}

/**
 * Custom hook that filters media items based on the selected media type filter.
 *
 * Filters a collection of media items to show only photos, videos, virtual tours, or all media
 * based on the active filter selection. Photos are defined as items that are neither videos
 * nor virtual tours.
 *
 * @param media - Array of media items containing both images and video embed data
 * @param activeMediaType - The currently selected media type filter (ALL, PHOTOS, VIDEOS, VIRTUAL_TOUR)
 * @returns Object containing the filtered media array
 */
export const useMediaFilter = ({
  media,
  activeMediaType,
}: UseMediaFilterProps) => {
  const filteredMedia = useMemo(() => {
    // If "ALL" is selected, return all media without filtering
    if (activeMediaType === MEDIA_TYPES.ALL) return media;

    // Filter media based on the selected media type
    return media.filter((item) => {
      switch (activeMediaType) {
        case MEDIA_TYPES.VIRTUAL_TOUR:
          // Only return items that are virtual tours
          return item.embed_type === MEDIA_TYPES.VIRTUAL_TOUR;
        case MEDIA_TYPES.VIDEOS:
          // Only return items that are videos
          return item.embed_type === MEDIA_TYPES.VIDEO;
        case MEDIA_TYPES.PHOTOS:
          // Return items that are neither virtual tours nor videos (i.e., regular photos)
          return (
            item.embed_type !== MEDIA_TYPES.VIRTUAL_TOUR &&
            item.embed_type !== MEDIA_TYPES.VIDEO
          );
        default:
          // Fallback: return all items if unknown media type
          return true;
      }
    });
  }, [media, activeMediaType]);

  return {
    filteredMedia,
  };
};
